import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import {
  LayoutRectangle,
  NativeSyntheticEvent,
  Platform,
  TextInput as RNTextInput,
  TextInputChangeEventData,
} from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Experiment } from 'components/experiment';
import { TextInput } from 'components/ucl/text-input';
import { GraphQLErrorMessages } from 'enums/graphql';
import { ProviderType } from 'generated/graphql-gateway';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useErrorModal from 'hooks/use-error-modal';
import AppleAuthButton from 'pages/authentication/components/social-auth-buttons/apple/apple-auth-button';
import FacebookAuthButton from 'pages/authentication/components/social-auth-buttons/facebook/facebook-auth-button';
import GoogleAuthButton from 'pages/authentication/components/social-auth-buttons/google/google-auth-button';
import { UserAuth } from 'pages/authentication/components/social-auth-buttons/types';
import { EmailIcon } from 'pages/authentication/sign-in/EmailIcon';
import { HttpErrorCodes } from 'remote/constants';
import { useAuthContext } from 'state/auth';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { ModalAuthScreen } from 'state/auth/types';
import { RunningExperiments } from 'state/experiments';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useScrollContext } from 'state/scroll';
import { useUIContext } from 'state/ui';
import { parseGraphQLErrorCodes } from 'utils/errors';
import { GraphQLErrorCodes } from 'utils/errors/types';
import { isEmailValid } from 'utils/form';
import formatListOfWords, { JoinType } from 'utils/intl/formatListOfWords';
import LocalStorage from 'utils/local-storage';
import { Keys } from 'utils/local-storage/constants';
import logger from 'utils/logger';
import { OTPAuthDeliveryMethod } from 'utils/otp';
import { routes } from 'utils/routing';

import { IAuthCampaign } from '../components/auth-campaigns/hooks/use-auth-campaign';

import { LabelStyled, OrSeparator, StyledButtonWidthLimit, TextInputWrapper } from './styled';

const { All, SMS, Email, None } = OTPAuthDeliveryMethod;

// Additional Y amount for show email input and submit button
const ADDITIONAL_SCROLL_Y = 50;

// we want to use the OTP flag value to adjust our messaging
const useFormattedMessages = () => {
  const { formatMessage } = useIntl();
  return (flagValue: OTPAuthDeliveryMethod) => {
    switch (flagValue) {
      case All:
        const combined = [
          formatMessage({ id: 'emailAddress' }),
          formatMessage({ id: 'phoneNumber' }),
        ];
        const combinedTitle = formatListOfWords({
          formatMessage,
          list: combined,
          joinType: JoinType.DISJUNCTION,
        });
        return [
          combinedTitle,
          formatMessage({ id: 'emailOrPhoneNumberRequiredError' }),
          formatMessage({ id: 'notValidEmailOrPhoneNumberError' }),
        ];
      case SMS:
        return [
          formatMessage({ id: 'phoneNumber' }),
          formatMessage({ id: 'phoneNumberRequiredError' }),
          formatMessage({ id: 'notValidPhoneNumberError' }),
        ];
      case Email:
      case None:
      default:
        return [
          formatMessage({ id: 'emailAddress' }),
          formatMessage({ id: 'emailRequiredError' }),
          formatMessage({ id: 'notValidEmailError' }),
        ];
    }
  };
};

interface ISignInFormProps {
  authCampaignExtraFields?: IAuthCampaign['extraFields'];
  disableEmailInput?: boolean;
  onPreSignIn?: () => void | Promise<void>;
  onSuccess?: () => void | Promise<void>;
  shouldDisplaySignInSuccessMessage?: boolean;
}

export const SignInForm = ({
  authCampaignExtraFields,
  disableEmailInput,
  onPreSignIn,
  onSuccess,
  shouldDisplaySignInSuccessMessage = true,
}: ISignInFormProps) => {
  const {
    isAuthenticated,
    signIn,
    socialLogin,
    setModalAuthState,
    modalAuthIsOpen,
    originLocation,
    modalAuthState,
    user,
    setIsReAuthenticating,
    setOriginLocation,
  } = useAuthContext();

  const { logEvent } = useMParticleContext();
  const chooseMessages = useFormattedMessages();
  const { formatMessage } = useIntl();
  const { attemptedSignUpWithExistingEmail, errorMessage: initialErrorMessage } = useAuthFlow();

  const { logRBIEvent } = useMParticleContext();

  const { userEmail } = useUIContext();
  const { signUpEmail } = useLocationContext();

  const emailInputEl = useRef<RNTextInput>(null);
  const enableUserNotFoundMaskAuthFlow = useFlag(
    LaunchDarklyFlag.ENABLE_USER_NOT_FOUND_MASK_ON_AUTH_FLOW
  );
  const isGoogleAuthEnabled = useFlag(LaunchDarklyFlag.ENABLE_SOCIAL_LOGIN_GOOGLE);
  const isAppleAuthEnabled = useFlag(LaunchDarklyFlag.ENABLE_SOCIAL_LOGIN_APPLE);
  const isFacebookAuthEnabled = useFlag(LaunchDarklyFlag.ENABLE_SOCIAL_LOGIN_FACEBOOK);
  const isOTPFlowEnabled = useFlag(LaunchDarklyFlag.ENABLE_OTP_LOGIN);

  const { navigate } = useNavigation();
  const isAnySocialLoginEnabled =
    isAppleAuthEnabled || isGoogleAuthEnabled || isFacebookAuthEnabled;
  const defaultEmail = signUpEmail || userEmail || '';
  const [emailInput, setEmailInput] = useState(defaultEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingFacebook, setIsLoadingFacebook] = useState(false);
  const [isLoadingApple, setIsLoadingApple] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    modalAuthState?.errorMessage || initialErrorMessage
  );
  const [shouldDisableAuthButtons, setShouldDisableAuthButtons] = useState(false);

  useLogUserErrorMessage({ message: errorMessage, severity: LogUserErrorSeverity.Recoverable });

  const [ErrorDialog, openErrDialog, dismissDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Sign In Failure',
  });

  const { setUserEmail } = useUIContext();

  const [EmailDoesNotMatchErrorDialog, openEmailDoesNotMatchErrDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Social login attempt with different email',
    onConfirm: () => {
      const signInErrorToast = {
        text: formatMessage({ id: 'authError' }),
        variant: 'negative',
      };

      navigate(routes.signIn, {
        state: { toast: signInErrorToast },
      });

      setModalAuthState({ screen: ModalAuthScreen.CLOSED });
      // This would only happen when re-authenticating via social login.
      setIsReAuthenticating(false);
      // Clears user email field in the sign-in form.
      setUserEmail('');
    },
  });

  const navigateOnSuccess = !modalAuthIsOpen;

  const aimToSignIn = useCallback(
    async ({ email = '', phoneNumber = '' }: any) => {
      try {
        setIsLoading(true);
        setShouldDisableAuthButtons(true);

        if (onPreSignIn) {
          await onPreSignIn();
        }

        await signIn({
          email,
          phoneNumber,
          ...(email && { navigateOnSuccessParams: { email } }),
          navigateOnSuccess,
          authenticationMethod: 'OTP',
        });

        logRBIEvent({
          name: 'Sign in with OTP Request',
          type: EventTypes.Other,
          attributes: {
            signUpType: email ? 'Email' : 'Phone Number',
          },
        });

        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        setShouldDisableAuthButtons(false);

        const createOtpError = enableUserNotFoundMaskAuthFlow
          ? GraphQLErrorCodes.CREATE_OTP_FAILED
          : GraphQLErrorCodes.AUTH_EMAIL_NOT_REGISTERED;

        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        const parsedErrors = parseGraphQLErrorCodes(error);

        if (
          parsedErrors.some(parsedError => parsedError.errorCode === createOtpError) ||
          // we need to handle both cases until all paths are migrated to apollo
          // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
          error.message === GraphQLErrorMessages.USER_NOT_FOUND
        ) {
          // if modal auth is open and the user is not found, we don't want to navigate to sign up page.
          // Instead, we want to show the sign up form in the modal.
          // Save the login email is crucial for this flow as we need to pre-populate the email field in the sign up form.
          // The legacy flow do that using the route params.
          if (!navigateOnSuccess) {
            setModalAuthState({ screen: ModalAuthScreen.SIGN_UP, user: { email } });
            return;
          }

          return navigate(routes.signUp, { state: { email } });
        }

        let message = formatMessage({ id: 'authError' });

        // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
        if (error?.graphQLErrors?.[0]?.extensions?.statusCode === HttpErrorCodes.TooManyRequests) {
          message = `${formatMessage({ id: 'maxAttemptsReached' })} ${formatMessage({
            id: 'pleaseTryAgainLater',
          })}`;
        }

        openErrDialog({
          // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
          error,
          message,
        });

        logger.warn({ error });
      }
    },
    [
      formatMessage,
      openErrDialog,
      signIn,
      setModalAuthState,
      navigateOnSuccess,
      enableUserNotFoundMaskAuthFlow,
      logRBIEvent,
      navigate,
      onPreSignIn,
    ]
  );

  useEffect(() => {
    if (attemptedSignUpWithExistingEmail && !isLoading) {
      setEmailInput(defaultEmail);
      aimToSignIn({ email: defaultEmail });
    }
  }, [defaultEmail, attemptedSignUpWithExistingEmail, aimToSignIn, isLoading]);

  const [labelMessage, requiredErrorMessage, invalidErrorMessage] = chooseMessages(
    OTPAuthDeliveryMethod.Email
  );

  const handleEmailChange = (ev: NativeSyntheticEvent<TextInputChangeEventData>) => {
    setErrorMessage(undefined);
    setModalAuthState({ errorMessage: '' });
    setEmailInput(ev.nativeEvent.text);
  };

  const parse = useCallback(
    async (input: string) => {
      const trimmedInput = input.trim();
      if (!trimmedInput) {
        setErrorMessage(requiredErrorMessage);
        return {};
      }

      const noLetters = /^[\0-9]{2,}$/.test(trimmedInput);

      const isEmail = isEmailValid(trimmedInput);

      if (!noLetters && !isEmail) {
        setErrorMessage(formatMessage({ id: 'notValidEmailError' }));
        return {};
      }

      if (isEmail) {
        return { email: trimmedInput };
      }

      setErrorMessage(invalidErrorMessage);
      // If there was an error, try to focus the input after an attempted sign in
      // this will force screen readers to read the alert even if it never left the screen
      if (emailInputEl.current) {
        emailInputEl.current.focus();
      }
      return {};
    },
    [invalidErrorMessage, requiredErrorMessage, formatMessage]
  );

  const handleSubmit = useCallback(async () => {
    const { email } = await parse(emailInput);

    if (email) {
      setErrorMessage('');
      setModalAuthState({ errorMessage: '' });
      aimToSignIn({ email });
    }
  }, [setModalAuthState, aimToSignIn, emailInput, parse]);

  const { scrollTo } = useScrollContext();

  const emailInputPosition = useRef<LayoutRectangle>();

  const handleEmailFocus = useCallback(() => {
    if (emailInputPosition.current && Platform.OS === 'android') {
      scrollTo({
        x: 0,
        y: emailInputPosition.current.y + emailInputPosition.current.height + ADDITIONAL_SCROLL_Y,
      });
    }
  }, [scrollTo]);

  const socialSignIn = useCallback(
    async (userInfo: UserAuth, providerType: ProviderType, eventName: CustomEventNames) => {
      try {
        if (onPreSignIn) {
          await onPreSignIn();
        }

        if (userInfo.email) {
          if (isAuthenticated && user?.details.email !== userInfo.email) {
            setIsLoadingApple(false);
            setIsLoadingFacebook(false);
            setIsLoadingGoogle(false);
            setShouldDisableAuthButtons(false);

            openEmailDoesNotMatchErrDialog({
              message: formatMessage({ id: 'emailAddressDoesNotMatch' }),
            });

            return;
          }

          if (LocalStorage.getItem(Keys.SOCIAL_LOGIN_USER_EMAIL) !== userInfo.email) {
            LocalStorage.setItem(Keys.SOCIAL_LOGIN_USER_EMAIL, userInfo.email);
          }
        }

        if (
          userInfo?.name?.trim()?.length &&
          LocalStorage.getItem(Keys.SOCIAL_LOGIN_USER_NAME) !== userInfo.name
        ) {
          LocalStorage.setItem(Keys.SOCIAL_LOGIN_USER_NAME, userInfo.name);
        }

        await socialLogin({
          providerType,
          providerToken: userInfo.token || '',
        });

        logEvent(eventName, EventTypes.Other);

        if (shouldDisplaySignInSuccessMessage) {
          const signInSuccessToast = {
            text: formatMessage({ id: 'signInSuccess' }),
            variant: 'positive',
          };

          if (!modalAuthIsOpen) {
            const path = originLocation || routes.base;
            setOriginLocation(null);
            navigate(path, {
              replace: true,
              state: { toast: signInSuccessToast },
            });
          }
        }

        LocalStorage.removeItem(Keys.SOCIAL_LOGIN_USER_NAME);

        if (onSuccess) {
          await onSuccess();
        }
      } catch (error) {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        const parsedErrors = parseGraphQLErrorCodes(error);

        if (
          // @ts-ignore
          parsedErrors.some(parsedError => parsedError.errorCode === 'USER_NOT_FOUND') ||
          // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
          error.message === GraphQLErrorMessages.USER_NOT_FOUND
        ) {
          const email = LocalStorage.getItem(Keys.SOCIAL_LOGIN_USER_EMAIL);
          const name = LocalStorage.getItem(Keys.SOCIAL_LOGIN_USER_NAME);

          if (modalAuthIsOpen) {
            setModalAuthState({ screen: ModalAuthScreen.SIGN_UP, user: { name, email } });
            return;
          }

          return navigate(routes.signUp, {
            state: {
              email,
              name,
              authenticationMethod: 'Social',
              socialAuthenticationType: providerType,
            },
          });
        }

        let message = formatMessage({ id: 'authError' });
        let title = undefined;

        if (
          // @ts-ignore
          parsedErrors.some(parsedError =>
            ['EMAIL_NOT_VERIFIED', 'USER_LOOKUP_FAILED'].includes(parsedError.errorCode)
          )
        ) {
          title = formatMessage({ id: 'userSocialLoginFailed' });
          message = formatMessage({ id: 'userAuthEmailNotVerified' });
        }

        // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
        if (error?.graphQLErrors?.[0]?.extensions?.statusCode === HttpErrorCodes.TooManyRequests) {
          message = `${formatMessage({ id: 'maxAttemptsReached' })} ${formatMessage({
            id: 'pleaseTryAgainLater',
          })}`;
        }

        openErrDialog({
          // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
          error,
          title,
          message,
          onConfirm: () => socialSignIn(userInfo, providerType, eventName),
          onDismiss: dismissDialog,
          confirmationText: formatMessage({ id: 'retry' }),
          dismissText: formatMessage({ id: 'close' }),
        });

        logger.warn({ error });
      }
    },
    [
      onPreSignIn,
      socialLogin,
      logEvent,
      shouldDisplaySignInSuccessMessage,
      originLocation,
      modalAuthIsOpen,
      onSuccess,
      isAuthenticated,
      user?.details.email,
      openErrDialog,
      formatMessage,
      navigate,
      dismissDialog,
      setModalAuthState,
    ]
  );

  const signInUpForm = (
    <>
      <TextInputWrapper>
        <TextInput
          accessibilityLabel={errorMessage ? errorMessage : labelMessage}
          testID="signin-email-input"
          errorMessage={errorMessage}
          label={labelMessage}
          required
          type="email"
          onChange={handleEmailChange}
          ref={emailInputEl}
          autoComplete="email"
          value={emailInput}
          autoCapitalize="none"
          keyboardType="email-address"
          returnKeyType="done"
          onSubmitEditing={handleSubmit}
          onLayout={ev => (emailInputPosition.current = ev.nativeEvent.layout)}
          onFocus={handleEmailFocus}
          placeholder={formatMessage({ id: 'emailAddress' })}
          isDisabled={!!disableEmailInput}
        />
      </TextInputWrapper>
      {authCampaignExtraFields}
      <StyledButtonWidthLimit>
        <ActionButton
          fullWidth
          testID="signin-button"
          isLoading={isLoading}
          disabled={shouldDisableAuthButtons}
          onPress={handleSubmit}
        >
          {formatMessage({ id: 'signInUp' })}
        </ActionButton>
      </StyledButtonWidthLimit>
    </>
  );

  const dividerComponent = (
    <Experiment
      flag={RunningExperiments.HideEmailAuth}
      control={isAnySocialLoginEnabled && <OrSeparator>{formatMessage({ id: 'or' })}</OrSeparator>}
    />
  );

  const formComponent = (
    <Experiment
      flag={RunningExperiments.HideEmailAuth}
      control={signInUpForm}
      treatment={showForm && signInUpForm}
    />
  );

  return (
    <>
      {isGoogleAuthEnabled && (
        <GoogleAuthButton
          isLoading={isLoadingGoogle}
          isDisabled={shouldDisableAuthButtons}
          onStart={() => {
            setIsLoadingGoogle(true);
            setShouldDisableAuthButtons(true);
          }}
          onSuccess={async userInfo => {
            await socialSignIn(
              userInfo,
              ProviderType.GOOGLE,
              CustomEventNames.SOCIAL_LOGIN_GOOGLE_COMPLETE
            );

            setIsLoadingGoogle(false);
          }}
          onError={error => {
            setIsLoadingGoogle(false);
            setShouldDisableAuthButtons(false);

            if (
              error.code !== 'COOKIE_CLOSE_POPUP' &&
              !error?.message?.includes('cancelled') &&
              !error?.message?.includes('canceled the sign in request')
            ) {
              openErrDialog({
                error,
                title: formatMessage({ id: 'userSocialLoginFailed' }),
                message: formatMessage({ id: 'userSocialLoginGenericErrorDescription' }),
              });
            }
          }}
        />
      )}
      {isFacebookAuthEnabled && (
        <FacebookAuthButton
          isLoading={isLoadingFacebook}
          isDisabled={shouldDisableAuthButtons}
          onStart={() => {
            setIsLoadingFacebook(true);
            setShouldDisableAuthButtons(true);
          }}
          onSuccess={async userInfo => {
            await socialSignIn(
              userInfo,
              ProviderType.FACEBOOK,
              CustomEventNames.SOCIAL_LOGIN_FACEBOOK_COMPLETE
            );

            setIsLoadingFacebook(false);
          }}
          onError={error => {
            setIsLoadingFacebook(false);
            setShouldDisableAuthButtons(false);

            if (error?.status === 'unknown' || error?.isCancelled) {
              return;
            }

            openErrDialog({
              error,
              title: formatMessage({ id: 'userSocialLoginFailed' }),
              message: formatMessage({ id: 'userSocialLoginGenericErrorDescription' }),
            });
          }}
        />
      )}
      {isAppleAuthEnabled && (
        <AppleAuthButton
          isLoading={isLoadingApple}
          isDisabled={shouldDisableAuthButtons}
          onStart={() => {
            setIsLoadingApple(true);
            setShouldDisableAuthButtons(true);
          }}
          onSuccess={async userInfo => {
            await socialSignIn(
              userInfo,
              ProviderType.APPLE,
              CustomEventNames.SOCIAL_LOGIN_APPLE_COMPLETE
            );

            setIsLoadingApple(false);
          }}
          onError={error => {
            setIsLoadingApple(false);

            if (
              error?.err?.error === 'popup_closed_by_user' ||
              error?.message?.includes('AuthorizationError')
            ) {
              return;
            }

            openErrDialog({
              error,
              title: formatMessage({ id: 'userSocialLoginFailed' }),
              message: formatMessage({ id: 'userSocialLoginGenericErrorDescription' }),
            });
          }}
        />
      )}
      <Experiment
        flag={RunningExperiments.HideEmailAuth}
        treatment={
          !showForm &&
          isOTPFlowEnabled && (
            <ActionButton
              eventAttributes={{ Name: 'Continue with Email' }}
              leftIcon={<EmailIcon />}
              fullWidth
              borderColor={Styles.color.black}
              marginY="$2"
              variant={ActionButtonVariants.OUTLINE}
              disabled={shouldDisableAuthButtons}
              onPress={() => setShowForm(true)}
            >
              <LabelStyled>{formatMessage({ id: 'continueWithEmail' })}</LabelStyled>
            </ActionButton>
          )
        }
      />
      {isOTPFlowEnabled && dividerComponent}
      {isOTPFlowEnabled && formComponent}
      <ErrorDialog />
      <EmailDoesNotMatchErrorDialog />
    </>
  );
};
